import { useEffect, useRef } from "react";
import { usePostHog } from "@posthog/react";
import type {
    ApiCommerceOffer,
    ApiCommerceStandaloneOfferV2,
} from "@bridge-applications/pb-types";
import { captureClientAnalytics } from "../helpers/analytics";
import { telegramStarsPrice } from "../helpers/commerce";
import { standaloneOfferPrice } from "../helpers/standaloneOffers";
import { POSTHOG_ENABLED } from "../../state/config";

/** Count actual visible presentations, not catalog fetches or background tabs. */
export function useCommerceOfferExposure(
    offer: ApiCommerceOffer | ApiCommerceStandaloneOfferV2 | null,
    surface: "BEACH_COIN_OFFER" | "BUNDLE_OFFER",
    isOpen: boolean,
    userId: string | null,
): void {
    const posthog = usePostHog();
    const seen = useRef<string | null>(null);
    const price =
        offer &&
        ("availability" in offer
            ? standaloneOfferPrice(offer)
            : telegramStarsPrice(offer));
    const code = offer?.offerCode;
    const revision = offer?.offerRevisionId;
    const amount = price?.unitAmount;
    useEffect(() => {
        const key =
            isOpen && userId && revision && amount
                ? `${userId}:${surface}:${revision}:${amount}`
                : null;
        if (!key) {
            seen.current = null;
            return;
        }
        const capture = () => {
            if (
                !POSTHOG_ENABLED ||
                !posthog ||
                document.visibilityState !== "visible" ||
                seen.current === key
            )
                return;
            captureClientAnalytics(posthog, "commerce_offer_viewed", {
                exposure_id: crypto.randomUUID(),
                offer_code: code!,
                offer_revision_id: revision!,
                price_xtr: amount!,
                surface,
            });
            seen.current = key;
        };
        capture();
        document.addEventListener("visibilitychange", capture);
        return () => document.removeEventListener("visibilitychange", capture);
    }, [posthog, userId, isOpen, surface, code, revision, amount]);
}
